import * as React from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import withStyles, { WithStylesProps } from 'react-jss';
import { ThemeType } from 'src/theme/jss-types';
import { useTranslation } from 'react-i18next';

const styles = (theme: ThemeType) => ({
  dropdownLoader: {
    display: 'flex',
    justifyContent: 'center',
    fontSize: '24px',
    padding: '10px',
  },
});

interface IProps extends WithStylesProps<typeof styles> {
  searchText: string;
  options: {
    key: string,
    label: string,
  }[];
  thresholdLength?: number;
  searchFunction: (query: string) => Promise<void>;
  filterNotRequired?: boolean;
  style?: any;
  suffixIcon?: any;
  className: any;
  placeholder: string;
  loading: boolean;
  [key: string]: any;
}

const SearchTagsInput = (props: IProps) => {
  const {
    classes, filterNotRequired, style, suffixIcon,
    searchText, options, searchFunction, thresholdLength, loading,
    className, placeholder, ...otherProps } = props;
  const { t } = useTranslation();

  const getInitialDropdownString = () => {
    return searchText || 'Type to start searching';
  };

  let searchTimeOut: any = null;
  const [dropdownSearchString, setDropdownSearchString] =
  React.useState<string>(getInitialDropdownString());

  const renderOptions = () => {
    return options.map((elem) =>
      <Select.Option key={elem.key} value={elem.key}>{elem.label}</Select.Option>);
  };

  const resetDropdownSearchString = () => {
    setDropdownSearchString(getInitialDropdownString());
  };

  const searchForData = async (query: string) => {
    resetDropdownSearchString();
    searchFunction(query);
  };

  const handleTextChange = (val) => {
    if (val.length >= (thresholdLength || 3)) {
      if (searchTimeOut) {
        clearTimeout(searchTimeOut);
      }
      searchTimeOut = setTimeout(() => {
        searchForData(val);
      }, 300);
    } else if (searchTimeOut) {
      resetDropdownSearchString();
      clearTimeout(searchTimeOut);
    }
  };

  React.useEffect(() => {
    if (options.length === 0) {
      setDropdownSearchString(t('No data found'));
    }
  }, [options]);

  return (
    <Select
      showSearch
      labelInValue
      suffixIcon={suffixIcon}
      placeholder={placeholder}
      style={style ?? {}}
      className={className}
      onFocus={resetDropdownSearchString}
      onSearch={handleTextChange}
      onInputKeyDown={(e) => {
        if (e.key === 'Enter' && !options?.length) {
          e.stopPropagation();
          e.preventDefault();
        }
      }}
      notFoundContent={
        loading ?
          <div className={classes.dropdownLoader}>
            <LoadingOutlined />
          </div>
        : t(dropdownSearchString)
      }
      optionFilterProp="children"
      filterOption={(inputVal, option: any) => {
        if (!inputVal || filterNotRequired) {
          return true;
        }
        const optionval = option.children ? option.children.toLowerCase() : '';
        return optionval.includes(inputVal.trim().toLowerCase());
      }}
      {...otherProps}
    >
      {
        renderOptions()
      }
    </Select>
  );
};

export default withStyles(styles, { injectTheme: true })(SearchTagsInput);
